/**
 * Group-deposit logic for online quotes.
 *
 * Shared by the public quote page (to DISPLAY the deposit due today) and
 * mirrored by the `createQuoteDepositIntent` cloud function (the AUTHORITATIVE
 * amount actually charged - the client-side figure is never trusted). The rules
 * match the quoting-engine PDF (see drawText.js):
 *
 *   - £50 per person standard deposit (groups under 8).
 *   - Groups of 8+ instead secure with a £100 group deposit today, CREDITED
 *     toward the per-person deposit; the rest of the deposit (£50pp - £100) is
 *     due within 28 days. This £100 (plus any up-front flights) is what's charged
 *     online for an 8+ group.
 *   - When travel is inside the 12-week window, the FULL balance is due today
 *     (any group size - the group-deposit option doesn't apply).
 *   - Otherwise the deposit is due today and the balance is due 12 weeks before
 *     travel.
 */
export const PER_PERSON_DEPOSIT = 50
export const FULL_BALANCE_WEEKS = 12
export const GROUP_SECURE_THRESHOLD = 8
export const GROUP_SECURE_DEPOSIT = 100
export const GROUP_SECURE_SECOND_INSTALMENT_DAYS = 28
const WEEK_MS = 7 * 24 * 60 * 60 * 1000

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100

/**
 * @param {{ groupSize: number, travelMillis: number, sellTotal: number, flightDepositGBP?: number, fullBalanceWeeks?: number }} input
 * @returns {{
 *   dueToday: number,            // amount charged by "Secure Quote & Book"
 *   fullBalanceDue: boolean,     // travel inside 12 weeks -> pay it all today
 *   groupDeposit: boolean,       // 8+ group on the £100 group-deposit flow
 *   groupSize: number,
 *   perPersonDeposit: number,
 *   groupSecureDeposit: number,      // the £100 group deposit (8+)
 *   secondInstalment: number,        // rest of the deposit due within 28 days (8+)
 *   secondInstalmentDays: number,    // 28
 *   flightDeposit: number,           // flight cost folded into today's deposit (group)
 *   flightDepositPerPerson: number,  // the same, per person - for client-facing copy
 *   perPersonDueToday: number,       // £50 + flight pp - what each traveller pays today (under 8)
 *   eligibleForGroupSecure: boolean,
 *   balanceDueWeeksBefore: number,
 *   balanceRemaining: number,
 * }}
 */
export function computeQuoteDeposit({ groupSize, travelMillis, sellTotal, flightDepositGBP = 0, fullBalanceWeeks = FULL_BALANCE_WEEKS }) {
	const size = Math.max(0, Math.floor(Number(groupSize) || 0))
	const total = round2(sellTotal)
	// Flights flagged "charge up front" are added to today's deposit so we can
	// ticket them immediately (only when a deposit is being taken - inside the
	// full-balance window the whole sell total, flights included, is due anyway).
	const flightDeposit = round2(flightDepositGBP)
	// Per-quote full-balance threshold (chosen at send time); falls back to the
	// standard 12 weeks for older quotes or out-of-range values. One deadline
	// drives both the "pay it all today" window and the balance-due-before-travel
	// date - keep this clamp identical to the authoritative server check.
	const weeks = Number(fullBalanceWeeks) > 0 ? Number(fullBalanceWeeks) : FULL_BALANCE_WEEKS
	// Millisecond comparison, matching the authoritative server check exactly
	// (the previous moment .diff("weeks") truncated to whole weeks, so the two
	// could disagree within the boundary week).
	const withinFullBalanceWindow = travelMillis ? travelMillis - Date.now() <= weeks * WEEK_MS : false

	if (withinFullBalanceWindow) {
		return {
			dueToday: total,
			fullBalanceDue: true,
			groupDeposit: false,
			groupSize: size,
			perPersonDeposit: 0,
			groupSecureDeposit: 0,
			secondInstalment: 0,
			secondInstalmentDays: GROUP_SECURE_SECOND_INSTALMENT_DAYS,
			flightDeposit: 0,
			flightDepositPerPerson: 0,
			perPersonDueToday: 0,
			eligibleForGroupSecure: false,
			balanceDueWeeksBefore: weeks,
			balanceRemaining: 0,
		}
	}

	const flightDepositPerPerson = size > 0 ? round2(flightDeposit / size) : 0
	const standardDepositTotal = round2(PER_PERSON_DEPOSIT * size)
	const balanceRemaining = round2(total - standardDepositTotal - flightDeposit)

	// Groups of 8+ secure with a £100 group deposit today (credited toward the
	// per-person deposit), with the rest of the deposit due within 28 days. Flights
	// flagged "charge up front" are still taken in full today to lock the fare.
	if (size >= GROUP_SECURE_THRESHOLD) {
		return {
			dueToday: round2(GROUP_SECURE_DEPOSIT + flightDeposit),
			fullBalanceDue: false,
			groupDeposit: true,
			groupSize: size,
			perPersonDeposit: PER_PERSON_DEPOSIT,
			groupSecureDeposit: GROUP_SECURE_DEPOSIT,
			secondInstalment: round2(standardDepositTotal - GROUP_SECURE_DEPOSIT),
			secondInstalmentDays: GROUP_SECURE_SECOND_INSTALMENT_DAYS,
			flightDeposit,
			flightDepositPerPerson,
			perPersonDueToday: 0,
			eligibleForGroupSecure: true,
			balanceDueWeeksBefore: weeks,
			balanceRemaining,
		}
	}

	// Under 8: the standard per-person deposit (plus any flights) is due today.
	const dueToday = round2(standardDepositTotal + flightDeposit)
	return {
		dueToday,
		fullBalanceDue: false,
		groupDeposit: false,
		groupSize: size,
		perPersonDeposit: PER_PERSON_DEPOSIT,
		groupSecureDeposit: GROUP_SECURE_DEPOSIT,
		secondInstalment: 0,
		secondInstalmentDays: GROUP_SECURE_SECOND_INSTALMENT_DAYS,
		flightDeposit,
		flightDepositPerPerson,
		perPersonDueToday: round2(PER_PERSON_DEPOSIT + flightDepositPerPerson),
		eligibleForGroupSecure: false,
		balanceDueWeeksBefore: weeks,
		balanceRemaining,
	}
}
